import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import Loader from '../components/Loader';
import SearchableSelect from '../components/SearchableSelect';
import ViewToggle from '../components/ViewToggle';
import { todaySGT, formatDate } from '../utils/date';
import { useViewPreference } from '../utils/viewPreference';
import { cardHeaderHue } from '../utils/cardTheme';
import { PAGE_SIZE_OPTIONS, buildPageList } from '../utils/pagination';

const EMPTY_FORM = {
  course_code: '', course_name: '', course_level: '', program_type: '',
  description: '', require_guiding_language: false, require_course_preview: false, status: 'Active',
};

export default function CourseCataloguePage() {
  const navigate = useNavigate();
  const { show } = useToast();
  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perms, setPerms] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [levelFilter, setLevelFilter] = useState('');

  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [showAllIntakes, setShowAllIntakes] = useState(false);
  const [view, setView] = useViewPreference('catalogue');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/course-master'),
      api.get('/course-levels'),
      api.get('/users/me/permissions'),
      api.get('/records/intakes').catch(() => ({ data: { data: [] } })),
    ])
      .then(([coursesRes, levelsRes, permsRes, intakesRes]) => {
        setCourses(coursesRes.data.data);
        setLevels(levelsRes.data.data);
        setPerms(permsRes.data.data.course_master || { view: false, create: false, update: false, delete: false });
        setIntakes(intakesRes.data.data || []);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load courses'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, statusFilter, levelFilter, pageSize, sortKey, sortDir]);

  const activeLevels = useMemo(() => levels.filter((l) => l.is_active), [levels]);
  const levelName = useCallback((code) => levels.find((l) => l.level_code === code)?.level_name || null, [levels]);

  const activeIntakesByCourse = useMemo(() => {
    const today = todaySGT();
    const map = {};
    intakes.forEach((i) => {
      if (!i.course_code || !i.is_active) return;
      const soc = i.soc_date ? String(i.soc_date).slice(0, 10) : null;
      const eoc = i.eoc_date ? String(i.eoc_date).slice(0, 10) : null;
      if (!soc || !eoc || today < soc || today > eoc) return;
      (map[i.course_code] || (map[i.course_code] = [])).push(i);
    });
    return map;
  }, [intakes]);

  const filteredCourses = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = courses.filter((c) => {
      if (term && !c.course_code.toLowerCase().includes(term) && !c.course_name.toLowerCase().includes(term)) return false;
      if (statusFilter && c.status !== statusFilter) return false;
      if (levelFilter && c.course_level !== levelFilter) return false;
      return true;
    });
    if (!sortKey) return list;
    const dir = sortDir === 'asc' ? 1 : -1;
    return list.slice().sort((a, b) => {
      const av = (a[sortKey] || '').toString().toLowerCase();
      const bv = (b[sortKey] || '').toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [courses, search, statusFilter, levelFilter, sortKey, sortDir]);

  const total = filteredCourses.length;
  const effectivePageSize = pageSize === 'All' ? total || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (pageSize === 'All') return filteredCourses;
    const start = (currentPage - 1) * effectivePageSize;
    return filteredCourses.slice(start, start + effectivePageSize);
  }, [filteredCourses, currentPage, effectivePageSize, pageSize]);

  const rangeStart = total === 0 ? 0 : (pageSize === 'All' ? 1 : (currentPage - 1) * effectivePageSize + 1);
  const rangeEnd = pageSize === 'All' ? total : Math.min(currentPage * effectivePageSize, total);

  const grouped = useMemo(() => {
    const sortedLevels = levels.slice().sort((a, b) => a.sequence - b.sequence);
    return sortedLevels
      .map((lvl) => ({ level: lvl, courses: pageRows.filter((c) => c.course_level === lvl.level_code) }))
      .concat([{ level: null, courses: pageRows.filter((c) => !c.course_level) }])
      .filter((g) => g.courses.length);
  }, [levels, pageRows]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalMode('create');
  };

  const openView = (row) => {
    setShowAllIntakes(false);
    setViewRow(row);
  };

  const openEdit = (row) => {
    setForm({
      course_code: row.course_code,
      course_name: row.course_name,
      course_level: row.course_level || '',
      program_type: row.program_type || '',
      description: row.description || '',
      require_guiding_language: !!row.require_guiding_language,
      require_course_preview: !!row.require_course_preview,
      status: row.status,
    });
    setFormError(null);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/course-master/${row.course_code}`, {
        course_name: row.course_name,
        course_level: row.course_level || null,
        program_type: row.program_type || null,
        description: row.description || null,
        require_guiding_language: !!row.require_guiding_language,
        require_course_preview: !!row.require_course_preview,
        status: row.status === 'Active' ? 'Inactive' : 'Active',
      });
      show(`Course ${row.status === 'Active' ? 'disabled' : 'enabled'}`, 'success');
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const isDuplicateCode = useMemo(() => {
    if (modalMode !== 'create') return false;
    const code = form.course_code.trim().toLowerCase();
    if (!code) return false;
    return courses.some((c) => c.course_code.toLowerCase() === code);
  }, [modalMode, form.course_code, courses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const codeBlank = !form.course_code || !form.course_code.trim();
    const nameBlank = !form.course_name || !form.course_name.trim();
    const levelBlank = !form.course_level || !form.course_level.trim();
    const programBlank = !form.program_type || !form.program_type.trim();
    const descriptionBlank = !form.description || !form.description.trim();
    if (codeBlank || nameBlank || levelBlank || programBlank || descriptionBlank) {
      setFormError('Course code, course name, course level, programme type, and description are required.');
      return;
    }
    if (modalMode === 'create' && isDuplicateCode) {
      setFormError('Course code already exists.');
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await api.post('/course-master', {
          course_code: form.course_code.trim(),
          course_name: form.course_name.trim(),
          course_level: form.course_level || null,
          program_type: form.program_type.trim() || null,
          description: form.description.trim() || null,
          require_guiding_language: !!form.require_guiding_language,
          require_course_preview: !!form.require_course_preview,
        });
        show('Course created', 'success');
      } else {
        await api.put(`/course-master/${form.course_code}`, {
          course_name: form.course_name.trim(),
          course_level: form.course_level || null,
          program_type: form.program_type.trim() || null,
          description: form.description.trim() || null,
          require_guiding_language: !!form.require_guiding_language,
          require_course_preview: !!form.require_course_preview,
          status: form.status,
        });
        show('Course updated', 'success');
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} course`);
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setLevelFilter('');
    setSortKey(null);
    setSortDir('asc');
  };

  if (!loading && !error && perms && !perms.view) {
    return (
      <div>
        <div className="sh-ey">COURSE CATALOGUE · COURSE MASTER</div>
        <div className="sh-ti">Course Master</div>
        <div className="alert"><i className="fa-solid fa-lock" /><div className="alert-t">You do not have access to Course Master.</div></div>
      </div>
    );
  }

  return (
    <div>
      <div className="sh-ey">COURSE CATALOGUE · COURSE MASTER</div>
      <div className="sh-ti">Course Portfolio</div>
      <div className="sh-de">Live course catalogue from the backend, grouped by course level — with real enrolment counts pulled from active student enrolments.</div>

      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div className="icon-field" style={{ flex: '1 1 240px', minWidth: 200 }}>
          <i className="fa-solid fa-magnifying-glass" />
          <input className="finput" placeholder="Search course code or name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="icon-field">
          <i className="fa-solid fa-filter" />
          <SearchableSelect
            className="finput" style={{ width: 170 }} value={statusFilter} onChange={setStatusFilter}
            placeholder="All Statuses"
            options={[{ value: '', label: 'All Statuses' }, { value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]}
          />
        </div>
        <div className="icon-field">
          <i className="fa-solid fa-layer-group" />
          <SearchableSelect
            className="finput" style={{ width: 190 }} value={levelFilter} onChange={setLevelFilter}
            placeholder="All Levels"
            options={[{ value: '', label: 'All Levels' }, ...activeLevels.map((l) => ({ value: l.level_code, label: l.level_name }))]}
          />
        </div>
        {view === 'tiles' && (
          <div className="icon-field" style={{ flex: '1 1 180px', minWidth: 160 }}>
            <i className="fa-solid fa-arrow-down-a-z" />
            <SearchableSelect
              className="finput" value={sortKey || ''} onChange={(val) => setSortKey(val || null)}
              placeholder="Default order"
              options={[
                { value: '', label: 'Default order' },
                { value: 'course_code', label: 'Sort: Course Code' },
                { value: 'course_name', label: 'Sort: Course Name' },
                { value: 'program_type', label: 'Sort: Programme Type' },
                { value: 'status', label: 'Sort: Status' },
              ]}
            />
          </div>
        )}
        {view === 'tiles' && sortKey && (
          <button type="button" className="btn btn-outline btn-toolbar" title={sortDir === 'asc' ? 'Ascending' : 'Descending'} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
            <i className={`fa-solid ${sortDir === 'asc' ? 'fa-arrow-up-wide-short' : 'fa-arrow-down-wide-short'}`} />
          </button>
        )}
        <button type="button" className="btn btn-outline btn-toolbar" onClick={clearFilters}><i className="fa-solid fa-arrow-rotate-left" /> Clear</button>
        <ViewToggle view={view} onChange={setView} />
        {perms?.create && (
          <button className="btn btn-primary btn-toolbar" style={{ marginLeft: 'auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Course</button>
        )}
      </div>

      {loading && <Loader label="Loading courses…" />}
      {error && !loading && <div className="alert"><i className="fa-solid fa-triangle-exclamation" /><div className="alert-t">{error}</div></div>}

      {!loading && !error && !courses.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <i className="fa-solid fa-book-open" style={{ fontSize: '2rem', color: 'var(--slate-lt)', marginBottom: '.75rem', display: 'block' }} />
          <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: '.35rem' }}>No courses yet</div>
          <div style={{ color: 'var(--slate)', marginBottom: '1rem' }}>Add your first course to get started.</div>
          {perms?.create && (
            <button className="btn btn-primary" style={{ margin: '0 auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Course</button>
          )}
        </div>
      )}

      {!loading && !error && view === 'tiles' && !!courses.length && !grouped.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--slate)' }}>
          No courses match the current search/filters.
        </div>
      )}

      {!loading && !error && view === 'tiles' && (
      <div className="view-fade" key="tiles">
      {grouped.map((g) => (
        <div key={g.level?.level_code || 'none'} style={{ marginBottom: '1.8rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem', marginBottom: '1rem' }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--ink)' }}>{g.level ? g.level.level_name : 'Ungrouped'}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--slate)', background: 'var(--cloud)', border: '1px solid var(--border)', borderRadius: 999, padding: '.14rem .65rem' }}>{g.courses.length} course{g.courses.length !== 1 ? 's' : ''}</div>
            {g.level && !g.level.is_active && (
              <span className="cc-level-warn">
                <i className="fa-solid fa-triangle-exclamation" /> This course level is inactive
              </span>
            )}
          </div>
          <div className="g3">
            {g.courses.map((c) => {
              const courseIntakes = activeIntakesByCourse[c.course_code] || [];
              const activeIntakeCount = courseIntakes.length;
              const enrolledCount = courseIntakes.reduce((sum, i) => sum + (Number(i.student_count) || 0), 0);
              return (
                <div key={c.course_code} className="cc-card">
                  <div className="cc-hd cc-hd-tint" style={{ '--h': cardHeaderHue(c.course_code) }}>
                    <div className="cc-em"><i className="fa-solid fa-book" /></div>
                    <div style={{ minWidth: 0 }}>
                      <div className="cc-code">{c.course_code}</div>
                      <div className="cc-ti">{c.course_name}</div>
                    </div>
                  </div>
                  <div className="cc-body">
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)', lineHeight: 1.62, marginBottom: '.72rem' }}>{c.description || 'No description yet.'}</div>
                    <div className="cc-m">
                      {c.program_type && <span><i className="fa-solid fa-tag" style={{ color: 'var(--slate-lt)' }} /> {c.program_type}</span>}
                      {!!c.require_guiding_language && <span title="This course requires a lecturer who can guide/teach in the course's target language." style={{ color: 'var(--sea)', fontWeight: 700, cursor: 'help' }}><i className="fa-solid fa-language" /> Guiding Language</span>}
                      {!!c.require_course_preview && <span title="Students must attend a preview session of this course before they can enrol in an intake." style={{ color: 'var(--sky)', fontWeight: 700, cursor: 'help' }}><i className="fa-solid fa-eye" /> Preview Required</span>}
                    </div>
                    <div style={{ marginTop: '.5rem', display: 'flex', gap: '1rem', fontSize: 'var(--text-xs)' }}>
                      <span
                        role={activeIntakeCount ? 'button' : undefined}
                        title={activeIntakeCount ? 'View intake capacity' : undefined}
                        style={{ display: 'flex', alignItems: 'center', gap: '.35rem', color: 'var(--slate)', cursor: activeIntakeCount ? 'pointer' : 'default' }}
                        onClick={activeIntakeCount ? () => openView(c) : undefined}
                      >
                        <i className="fa-solid fa-circle-check" style={{ color: 'var(--sea)' }} />
                        <strong style={{ color: 'var(--ink-s)' }}>{activeIntakeCount}</strong> active intake{activeIntakeCount !== 1 ? 's' : ''}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '.35rem', color: 'var(--slate)' }}>
                        <i className="fa-solid fa-user-group" style={{ color: 'var(--sky)' }} />
                        <strong style={{ color: 'var(--ink-s)' }}>{enrolledCount}</strong> enrolled
                      </span>
                    </div>
                  </div>
                  <div className="cc-foot">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }} onClick={(e) => e.stopPropagation()}>
                      {perms?.update ? (
                        <button
                          type="button"
                          className={`toggle ${c.status === 'Active' ? 'on' : ''}`}
                          role="switch"
                          aria-checked={c.status === 'Active'}
                          title={c.status === 'Active' ? 'Disable course' : 'Enable course'}
                          aria-label={c.status === 'Active' ? 'Disable course' : 'Enable course'}
                          onClick={() => toggleActive(c)}
                        />
                      ) : null}
                      <span className={`pill ${c.status === 'Active' ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${c.status === 'Active' ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {c.status}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem' }}>
                      <button type="button" className="btn btn-outline btn-sm" title="View course details" aria-label="View course details" onClick={() => openView(c)}><i className="fa-solid fa-eye" /></button>
                      {perms?.update && (
                        <button type="button" className="btn btn-outline btn-sm" title="Edit course" aria-label="Edit course" onClick={() => openEdit(c)}><i className="fa-solid fa-pen" /></button>
                      )}
                      <button type="button" className="btn btn-outline btn-sm" title="Go to Timetable to schedule an intake" onClick={() => navigate('/timetable')}><i className="fa-solid fa-calendar-plus" /> Schedule</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      </div>
      )}

      {view === 'table' && !loading && !error && !!filteredCourses.length && (
        <div className="card view-fade" key="table">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Level</th>
                  <th>Programme Type</th>
                  <th>Intakes</th>
                  <th>Enrolled</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((c) => {
                  const courseIntakes = activeIntakesByCourse[c.course_code] || [];
                  const activeIntakeCount = courseIntakes.length;
                  const enrolledCount = courseIntakes.reduce((sum, i) => sum + (Number(i.student_count) || 0), 0);
                  return (
                    <tr key={c.course_code}>
                      <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', fontWeight: 700, color: 'var(--ocean)' }}>{c.course_code}</span></td>
                      <td style={{ fontWeight: 600 }}>{c.course_name}</td>
                      <td>{levelName(c.course_level) || '—'}</td>
                      <td>{c.program_type || '—'}</td>
                      <td>{activeIntakeCount}</td>
                      <td>{enrolledCount}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                          {perms?.update ? (
                            <button
                              type="button"
                              className={`toggle ${c.status === 'Active' ? 'on' : ''}`}
                              role="switch"
                              aria-checked={c.status === 'Active'}
                              title={c.status === 'Active' ? 'Disable course' : 'Enable course'}
                              aria-label={c.status === 'Active' ? 'Disable course' : 'Enable course'}
                              onClick={() => toggleActive(c)}
                            />
                          ) : null}
                          <span className={`pill ${c.status === 'Active' ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${c.status === 'Active' ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {c.status}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '.4rem' }}>
                          <button type="button" className="btn btn-outline btn-sm" title="View course details" aria-label="View course details" onClick={() => openView(c)}><i className="fa-solid fa-eye" /></button>
                          {perms?.update && (
                            <button type="button" className="btn btn-outline btn-sm" title="Edit course" aria-label="Edit course" onClick={() => openEdit(c)}><i className="fa-solid fa-pen" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'table' && !loading && !error && !!courses.length && !filteredCourses.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--slate)' }}>
          No courses match the current search/filters.
        </div>
      )}

      {!loading && !error && !!total && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1.3rem', paddingTop: '1rem', borderTop: '1px solid var(--border-soft)' }}>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>
            Showing {rangeStart}–{rangeEnd} of {total}
          </div>
          <div className="icon-field" style={{ marginLeft: 'auto' }}>
            <SearchableSelect
              className="finput" style={{ width: 110 }} value={pageSize}
              onChange={(val) => setPageSize(val === 'All' ? 'All' : Number(val))}
              options={PAGE_SIZE_OPTIONS.map((opt) => ({ value: opt, label: String(opt) }))}
            />
          </div>
          {pageSize !== 'All' && totalPages > 1 && (
            <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage(1)}><i className="fa-solid fa-angles-left" /></button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}><i className="fa-solid fa-angle-left" /></button>
              {buildPageList(currentPage, totalPages).map((p, idx) => (
                p === '…'
                  ? <span key={`e${idx}`} style={{ padding: '0 .3rem', color: 'var(--slate-lt)' }}>…</span>
                  : (
                    <button
                      key={p}
                      type="button"
                      className={`btn btn-sm ${p === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
              ))}
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => p + 1)}><i className="fa-solid fa-angle-right" /></button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}><i className="fa-solid fa-angles-right" /></button>
            </div>
          )}
        </div>
      )}

      {modalMode && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeModal}>
          <form className="card modal" style={{ width: 520, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <div className="modal-header">
              <div className="modal-title">{modalMode === 'create' ? 'Add Course' : 'Edit Course'}</div>
              <button type="button" className="modal-close" onClick={closeModal} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
            {formError && <div className="lc-err">{formError}</div>}
            <div className="fg fg-tight">
              <label className="fl">Course Code *</label>
              <input
                className="finput"
                required
                disabled={modalMode === 'edit'}
                value={form.course_code}
                onChange={(e) => setForm((f) => ({ ...f, course_code: e.target.value }))}
              />
              {modalMode === 'edit' && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)', marginTop: '.25rem' }}>Course code cannot be changed after creation</div>
              )}
              {modalMode === 'create' && isDuplicateCode && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--red)', marginTop: '.25rem' }}>Course code already exists.</div>
              )}
            </div>
            <div className="fg fg-tight"><label className="fl">Course Name *</label><input className="finput" required value={form.course_name} onChange={(e) => setForm((f) => ({ ...f, course_name: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: '.8rem' }}>
              <div className="fg fg-tight" style={{ flex: 1 }}>
                <label className="fl">Course Level *</label>
                <SearchableSelect
                  className="finput" required value={form.course_level} onChange={(val) => setForm((f) => ({ ...f, course_level: val }))}
                  placeholder="Select level…"
                  options={activeLevels.map((l) => ({ value: l.level_code, label: l.level_name }))}
                />
              </div>
              <div className="fg fg-tight" style={{ flex: 1 }}>
                <label className="fl">Programme Type *</label>
                <input className="finput" required value={form.program_type} onChange={(e) => setForm((f) => ({ ...f, program_type: e.target.value }))} />
              </div>
            </div>
            <div className="fg fg-tight">
              <label className="fl">Description *</label>
              <textarea className="finput" rows={2} required value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="fg fg-tight" style={{ display: 'flex', alignItems: 'center', gap: '1.3rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <input type="checkbox" id="req-lang" checked={form.require_guiding_language} onChange={(e) => setForm((f) => ({ ...f, require_guiding_language: e.target.checked }))} />
                <label htmlFor="req-lang" className="fl" style={{ margin: 0 }} title="Requires a lecturer able to guide/teach in the course's target language">Requires guiding language</label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <input type="checkbox" id="req-preview" checked={form.require_course_preview} onChange={(e) => setForm((f) => ({ ...f, require_course_preview: e.target.checked }))} />
                <label htmlFor="req-preview" className="fl" style={{ margin: 0 }} title="Students must attend a preview session before enrolling in an intake">Requires course preview</label>
              </div>
            </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeModal}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || (modalMode === 'create' && isDuplicateCode)}>
                <i className={`fa-solid ${submitting ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                {submitting ? 'Saving…' : modalMode === 'create' ? 'Create Course' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {viewRow && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setViewRow(null)}>
          <div className="card modal" style={{ width: 520, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <div className="vw-hd">
              <div className="vw-em"><i className="fa-solid fa-book" /></div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--ocean)', letterSpacing: '.04em' }}>{viewRow.course_code}</div>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--ink)' }}>{viewRow.course_name}</div>
              </div>
              <span className={`pill ${viewRow.status === 'Active' ? 'p-ok' : 'p-draft'}`} style={{ marginLeft: 'auto', flexShrink: 0 }}><i className={`fa-solid ${viewRow.status === 'Active' ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {viewRow.status}</span>
              <button type="button" className="modal-close" onClick={() => setViewRow(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <div className="vw-grid">
                <div className="fg"><label className="fl">Course Level</label><div className="vw-val">{levelName(viewRow.course_level) || '—'}</div></div>
                <div className="fg"><label className="fl">Programme Type</label><div className="vw-val">{viewRow.program_type || '—'}</div></div>
                <div className="fg fg-full"><label className="fl">Description</label><div className="vw-val" style={{ whiteSpace: 'pre-wrap' }}>{viewRow.description || '—'}</div></div>
                <div className="fg fg-full">
                  <label className="fl">Requirements</label>
                  <div className="vw-flags">
                    <span
                      className={`pt ${viewRow.require_guiding_language ? 'pt-guide' : ''}`}
                      style={!viewRow.require_guiding_language ? { background: 'var(--chip-bg)', color: 'var(--slate)', border: '1px solid var(--border)' } : undefined}
                      title="Requires a lecturer able to guide/teach in the course's target language"
                    >
                      <i className="fa-solid fa-language" /> Guiding Language {viewRow.require_guiding_language ? 'required' : 'not required'}
                    </span>
                    <span
                      className="pt"
                      style={viewRow.require_course_preview ? { background: 'var(--sky-dim)', color: 'var(--sky)', border: '1px solid rgba(12,74,110,.18)' } : { background: 'var(--chip-bg)', color: 'var(--slate)', border: '1px solid var(--border)' }}
                      title="Students must attend a preview session before enrolling in an intake"
                    >
                      <i className="fa-solid fa-eye" /> Course Preview {viewRow.require_course_preview ? 'required' : 'not required'}
                    </span>
                  </div>
                </div>
                <div className="fg fg-full">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label className="fl" style={{ marginBottom: 0 }}>Active Intakes</label>
                    {(() => {
                      const viewIntakes = activeIntakesByCourse[viewRow.course_code] || [];
                      if (viewIntakes.length <= 1) return null;
                      return (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '.15rem .5rem' }}
                          onClick={() => setShowAllIntakes((v) => !v)}
                        >
                          {showAllIntakes ? 'Hide' : `Show all (${viewIntakes.length})`}
                        </button>
                      );
                    })()}
                  </div>
                  {(() => {
                    const viewIntakes = activeIntakesByCourse[viewRow.course_code] || [];
                    if (!viewIntakes.length) return <div className="vw-val" style={{ color: 'var(--slate)', marginTop: '.35rem' }}>No active intakes right now.</div>;
                    const shown = showAllIntakes ? viewIntakes : viewIntakes.slice(0, 1);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', marginTop: '.35rem' }}>
                        {shown.map((i) => {
                          const occupied = i.occupied_count ?? i.student_count ?? 0;
                          return (
                            <div key={i.intake_code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem', padding: '.5rem .7rem', background: 'var(--cloud)', border: '1px solid var(--border-soft)', borderRadius: 8 }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--ocean)' }}>{i.intake_code}</div>
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>{formatDate(i.soc_date)} → {formatDate(i.eoc_date)}</div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontWeight: 700, color: 'var(--ink-s)' }}>{occupied}/{i.capacity ?? '—'}</div>
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>capacity</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setViewRow(null)}>Close</button>
              {perms?.update && (
                <button type="button" className="btn btn-primary" onClick={() => { setViewRow(null); openEdit(viewRow); }}><i className="fa-solid fa-pen" /> Edit Course</button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
